import React, { useState } from "react";
import { DEMO_AI_MODULES, DEMO_PROFILES, DEMO_PROPERTIES, DEMO_REVIEWS } from "../../config/demoData";

const colors = {
  blue: "#0f6fff",
  green: "#10b981",
  text: "#0f172a",
  muted: "#52647b",
  border: "#dbeafe",
  surface: "#ffffff",
};

const card = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: 16,
  padding: 16,
  boxSizing: "border-box",
  minWidth: 0,
  display: "grid",
  gap: 10,
};

const button = {
  minHeight: 44,
  borderRadius: 12,
  border: 0,
  padding: "11px 14px",
  cursor: "pointer",
  fontWeight: 900,
  boxSizing: "border-box",
};

const primaryButton = {
  ...button,
  color: "#fff",
  background: `linear-gradient(135deg, ${colors.blue}, ${colors.green})`,
};

const secondaryButton = {
  ...button,
  color: colors.blue,
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
};

function Pill({ children, green = false }) {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      borderRadius: 999,
      padding: "5px 9px",
      fontSize: 11,
      fontWeight: 900,
      color: green ? "#047857" : colors.blue,
      background: green ? "#ecfdf5" : "#eff6ff",
      border: `1px solid ${green ? "#bbf7d0" : "#bfdbfe"}`,
    }}>
      {children}
    </span>
  );
}

function Stars({ rating }) {
  return <span style={{ color: "#f59e0b", letterSpacing: 0 }}>{Array.from({ length: 5 }, (_, index) => index < Math.round(rating) ? "★" : "☆").join("")}</span>;
}

export default function PreviewMode({ isPhoneMobile, onCreateAccount, onSignIn, onExit }) {
  const [tab, setTab] = useState("home");
  const [selectedPropertyId, setSelectedPropertyId] = useState(DEMO_PROPERTIES[0].id);
  const [selectedProfileId, setSelectedProfileId] = useState(DEMO_PROFILES[0].id);
  const selectedProperty = DEMO_PROPERTIES.find((property) => property.id === selectedPropertyId);
  const selectedProfile = DEMO_PROFILES.find((profile) => profile.id === selectedProfileId);
  const tabs = ["home", "reviews", "profiles", "ai"];

  const renderProperty = (property) => (
    <button
      key={property.id}
      type="button"
      onClick={() => setSelectedPropertyId(property.id)}
      style={{
        ...card,
        textAlign: "left",
        cursor: "pointer",
        borderColor: selectedPropertyId === property.id ? "#93c5fd" : colors.border,
        boxShadow: selectedPropertyId === property.id ? "0 10px 28px rgba(15,111,255,0.11)" : "none",
        width: "100%",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <Pill>Demo Property</Pill>
        {property.forRent ? <Pill green>For Rent</Pill> : null}
      </div>
      <strong style={{ color: colors.text, fontSize: 17 }}>{property.title}</strong>
      <span style={{ color: colors.muted, fontSize: 13 }}>{property.address}, {property.city}, {property.state}</span>
      <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap", color: colors.muted, fontSize: 13 }}>
        <Stars rating={property.rating} />
        <span>{property.rating.toFixed(1)} ({property.reviewCount})</span>
        {property.forRent ? <strong style={{ color: "#047857" }}>${property.monthlyRent.toLocaleString()}/mo</strong> : null}
      </div>
    </button>
  );

  const renderLockedAction = (label) => (
    <button type="button" style={{ ...secondaryButton, opacity: 0.72, width: "100%" }} onClick={onCreateAccount}>
      {label} - Create Account
    </button>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f7fbff", color: colors.text, boxSizing: "border-box", paddingBottom: isPhoneMobile ? 92 : 24, overflowX: "hidden" }}>
      <header style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        background: "rgba(255,255,255,0.96)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid #e2e8f0",
      }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: isPhoneMobile ? "12px" : "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <strong style={{ fontSize: 26, color: colors.blue }}>Rent<span style={{ color: colors.green }}>Duo</span></strong>
            <Pill>Preview Mode</Pill>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" style={{ ...secondaryButton, padding: "9px 12px" }} onClick={onSignIn}>Sign In</button>
            <button type="button" style={{ ...primaryButton, padding: "9px 12px" }} onClick={onCreateAccount}>Create Account</button>
            <button type="button" style={{ ...secondaryButton, padding: "9px 12px" }} onClick={onExit}>Exit Preview</button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1120, margin: "0 auto", padding: isPhoneMobile ? "14px 12px" : "22px", display: "grid", gap: 16 }}>
        <section style={{
          ...card,
          padding: isPhoneMobile ? 18 : 24,
          background: "linear-gradient(135deg, #eaf3ff, #eafbf3)",
        }}>
          <Pill green>Explore Demo</Pill>
          <h1 style={{ margin: 0, fontSize: isPhoneMobile ? 25 : 32, lineHeight: 1.15 }}>Rental trust before you sign.</h1>
          <p style={{ margin: 0, color: colors.muted, lineHeight: 1.55 }}>
            Explore sample properties, public reviews, trusted profile experiences, and RentDuo AI insights without creating an account.
          </p>
          <strong style={{ color: colors.blue, fontSize: 14 }}>Create a free account to unlock full RentDuo features.</strong>
        </section>

        <nav style={{ display: "flex", flexWrap: "wrap", gap: 8, width: "100%" }}>
          {tabs.map((value) => (
            <button key={value} type="button" onClick={() => setTab(value)} style={{ ...(tab === value ? primaryButton : secondaryButton), flex: isPhoneMobile ? "1 1 calc(50% - 8px)" : "0 1 auto", minWidth: isPhoneMobile ? 0 : 120 }}>
              {value === "ai" ? "AI Center" : value.charAt(0).toUpperCase() + value.slice(1)}
            </button>
          ))}
        </nav>

        {tab === "home" ? (
          <div style={{ display: "grid", gridTemplateColumns: isPhoneMobile ? "1fr" : "minmax(280px, 0.95fr) minmax(340px, 1.1fr)", gap: 14, alignItems: "start" }}>
            <section style={{ display: "grid", gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: 20 }}>Sample Properties</h2>
              {DEMO_PROPERTIES.map(renderProperty)}
            </section>
            <section style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <Pill>Demo Property</Pill>
                <Pill green>{selectedProperty.risk.level} Risk</Pill>
              </div>
              <h2 style={{ margin: 0, fontSize: 21 }}>{selectedProperty.title}</h2>
              <p style={{ margin: 0, color: colors.muted }}>{selectedProperty.address}, {selectedProperty.city}, {selectedProperty.state}</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Pill>{selectedProperty.bedrooms} Bed</Pill>
                <Pill>{selectedProperty.bathrooms} Bath</Pill>
                {selectedProperty.forRent ? <Pill green>${selectedProperty.monthlyRent.toLocaleString()}/mo</Pill> : null}
              </div>
              <div style={{ ...card, background: "#f8fbff", padding: 12 }}>
                <strong>AI Property Risk Score: {selectedProperty.risk.score}/100</strong>
                <p style={{ margin: 0, color: colors.muted, lineHeight: 1.45 }}>{selectedProperty.risk.summary}</p>
              </div>
              <div style={{ ...card, background: "#f0fdf4", padding: 12 }}>
                <strong>Neighborhood Insight</strong>
                <p style={{ margin: 0, color: colors.muted, lineHeight: 1.45 }}>{selectedProperty.neighborhood}</p>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isPhoneMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                {renderLockedAction("Save Property")}
                {renderLockedAction("Message Contact")}
              </div>
            </section>
          </div>
        ) : null}

        {tab === "reviews" ? (
          <section style={{ display: "grid", gap: 12 }}>
            <h2 style={{ margin: 0, fontSize: 20 }}>Public Sample Reviews</h2>
            {DEMO_REVIEWS.map((review) => {
              const property = DEMO_PROPERTIES.find((item) => item.id === review.propertyId);
              return (
                <div key={review.id} style={card}>
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <Pill>Sample Review</Pill>
                    <Stars rating={review.rating} />
                  </div>
                  <strong>{property.title}</strong>
                  <p style={{ margin: 0, color: colors.muted, lineHeight: 1.55 }}>{review.text}</p>
                  <span style={{ color: colors.muted, fontSize: 12 }}>{review.authorName} - {review.date}</span>
                </div>
              );
            })}
            {renderLockedAction("Leave a Review")}
          </section>
        ) : null}

        {tab === "profiles" ? (
          <div style={{ display: "grid", gridTemplateColumns: isPhoneMobile ? "1fr" : "minmax(270px, 0.8fr) minmax(320px, 1fr)", gap: 14 }}>
            <section style={{ display: "grid", gap: 10 }}>
              {DEMO_PROFILES.map((profile) => (
                <button type="button" key={profile.id} onClick={() => setSelectedProfileId(profile.id)} style={{ ...card, textAlign: "left", cursor: "pointer", width: "100%", borderColor: profile.id === selectedProfileId ? "#93c5fd" : colors.border }}>
                  <Pill>Demo Profile</Pill>
                  <strong>{profile.displayName}</strong>
                  <span style={{ color: colors.muted }}>{profile.role} - {profile.city}, {profile.state}</span>
                </button>
              ))}
            </section>
            <section style={card}>
              <div style={{ height: 86, borderRadius: 12, background: "linear-gradient(120deg, #0f6fff, #10b981)" }} />
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <h2 style={{ margin: 0 }}>{selectedProfile.displayName}</h2>
                <Pill green>Verified</Pill>
              </div>
              <p style={{ margin: 0, color: colors.muted }}>{selectedProfile.role} - {selectedProfile.city}, {selectedProfile.state}</p>
              <Pill>{selectedProfile.status}</Pill>
              <strong>Trust Score: {selectedProfile.trustScore}</strong>
              <p style={{ margin: 0, color: colors.muted, lineHeight: 1.5 }}>{selectedProfile.bio}</p>
              {renderLockedAction("Message Profile")}
            </section>
          </div>
        ) : null}

        {tab === "ai" ? (
          <section style={{ display: "grid", gap: 12 }}>
            <div style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <Pill>Powered by RentDuo AI</Pill>
                <Pill green>Preview Mode</Pill>
              </div>
              <h2 style={{ margin: 0 }}>AI Center</h2>
              <p style={{ margin: 0, color: colors.muted }}>Explore sample rental intelligence. Premium analysis tools unlock after account creation.</p>
              <small style={{ color: colors.muted }}>AI guidance only. Not legal or financial advice.</small>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isPhoneMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 }}>
              <div style={card}>
                <Pill>AI Property Risk Score</Pill>
                <strong>{selectedProperty.title}: {selectedProperty.risk.level} Risk</strong>
                <p style={{ margin: 0, color: colors.muted }}>{selectedProperty.risk.summary}</p>
                {Object.entries(selectedProperty.risk.categories).map(([label, score]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #eef2ff" }}>
                    <span>{label}</span><strong>{score}</strong>
                  </div>
                ))}
              </div>
              <div style={card}>
                <Pill>AI Neighborhood Intelligence</Pill>
                <strong>{selectedProperty.city}, {selectedProperty.state}</strong>
                <p style={{ margin: 0, color: colors.muted, lineHeight: 1.5 }}>{selectedProperty.neighborhood}</p>
              </div>
              {DEMO_AI_MODULES.map((module) => (
                <div key={module.title} style={card}>
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <strong>{module.title}</strong>
                    <Pill>{module.label}</Pill>
                  </div>
                  <p style={{ margin: 0, color: colors.muted, lineHeight: 1.5 }}>{module.text}</p>
                  <button type="button" style={{ ...secondaryButton, cursor: "default", opacity: 0.76 }}>{module.label}</button>
                </div>
              ))}
            </div>
            <button type="button" style={primaryButton} onClick={onCreateAccount}>Create Account to Unlock AI Tools</button>
          </section>
        ) : null}
      </main>
    </div>
  );
}
